<template>
  <v-menu
    ref="menu"
    v-model="menu"
    :content-class="contentClass"
    :close-on-content-click="false"
    :return-value.sync="date"
    lazy
    transition="scale-transition"
    :disabled="disabled"
    offset-y
    :nudge-bottom="nudgeBottom"
    :nudge-right="nudgeRight"
  >
    <slot slot="activator" />
    <v-date-picker
      v-model="date"
      no-title
      :min="minDate"
      :max="maxDate"
      :show-current="!showSelectedDate"
      :allowed-dates="allowedDates"
    >
      <v-spacer />
      <v-btn
        v-if="clearable"
        flat
        color="error"
        @click="updateValue(undefined)"
      >
        Clear
      </v-btn>
      <v-btn flat color="primary" @click="closeMenu"> Cancel </v-btn>
      <v-btn flat color="primary" @click="updateValue(date)"> OK </v-btn>
    </v-date-picker>
  </v-menu>
</template>

<script>
/*
This component is used to select dates by opening a small calendar in a modal
*/
export default {
  name: 'CalendarPopup',
  props: {
    value: {
      type: [Date, String],
      default() {
        return undefined;
      },
    },
    minDate: {
      type: String,
      default() {
        return '0001-01-01';
      },
    },
    maxDate: {
      type: String,
      default() {
        return '';
      },
    },
    clearable: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    showSelectedDate: {
      type: Boolean,
    },
    allowedDates: {
      type: Function,
      default() {
        return () => true;
      },
    },
    contentClass: {
      type: String,
      default() {
        return '';
      },
    },
  },
  data() {
    return {
      menu: false,
      date: this.value,
      nudgeBottom: '',
      nudgeRight: '',
    };
  },
  methods: {
    updateValue(newDate) {
      this.date = newDate;
      this.$refs.menu.save(newDate);
      this.$emit('input', this.date);
    },
    closeMenu() {
      this.menu = false;
    },
    focusMenu() {
      this.menu = true;
    },
  },
};
</script>

<style scoped>
.v-menu__content {
  position: absolute !important;
}
</style>

<style>
/* ── Dark mode: v-date-picker ────────────────────────────────────────────────
   Vuetify teleports the menu/picker to a body portal, so these must be
   global (non-scoped). All values use Variables.css tokens.
   CSS-based theming reacts to the html.dark class change automatically —
   no JS required for dynamic switching.                                       */

/* Picker shell */
html.dark .v-picker.theme--light,
html.dark .v-picker__body {
  background: var(--surface) !important;
}

/* Header: month/year label + nav arrow buttons */
html.dark .v-date-picker-header {
  background: var(--surface) !important;
}
html.dark .v-date-picker-header .v-btn {
  color: var(--text2) !important;
}
html.dark .v-date-picker-header__value button {
  color: var(--text2) !important;
}

/* Calendar grid */
html.dark .v-date-picker-table {
  background: var(--surface) !important;
}
html.dark .v-date-picker-table th {
  color: var(--text2) !important;
}

html.dark
  .v-date-picker-table
  .v-btn:not(.v-btn--active):not(.v-btn--disabled) {
  color: var(--text2) !important;
}
html.dark
  .v-date-picker-table
  .v-btn:not(.v-btn--active):not(.v-btn--disabled):hover {
  background: var(--sb-subtle) !important;
}
html.dark .v-date-picker-table .v-btn.v-btn--active {
  background-color: var(--blue) !important;
  color: #ffffff !important;
}
html.dark .v-date-picker-table .v-btn.v-btn--disabled {
  color: var(--text3) !important;
}
/* Today ring */
html.dark .v-date-picker-table__current {
  border-color: var(--blue) !important;
}

/* Year picker list */
html.dark .v-date-picker-years {
  background: var(--surface) !important;
}
html.dark .v-date-picker-years li {
  color: var(--text2) !important;
}
html.dark .v-date-picker-years li.active {
  color: var(--blue) !important;
}

/* Action buttons (OK / Cancel / Clear) */
html.dark .v-picker__actions {
  background: var(--surface) !important;
  border-top: 1px solid var(--border);
}
html.dark .v-picker__actions .v-btn {
  color: var(--blue) !important;
}
html.dark .v-picker__actions .error--text {
  color: var(--sb-error) !important;
}

/* Elevation + border for the popup card */
html.dark .v-menu__content .v-picker {
  border: 1px solid var(--border) !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4) !important;
}

/* ── Auto dark (system preference) ──────────────────────────────────────── */
@media (prefers-color-scheme: dark) {
  html:not(.light):not(.dark) .v-picker.theme--light,
  html:not(.light):not(.dark) .v-picker__body {
    background: var(--surface) !important;
  }

  html:not(.light):not(.dark) .v-date-picker-header {
    background: var(--surface) !important;
  }
  html:not(.light):not(.dark) .v-date-picker-header .v-btn {
    color: var(--text2) !important;
  }
  html:not(.light):not(.dark) .v-date-picker-header__value button {
    color: var(--text2) !important;
  }

  html:not(.light):not(.dark) .v-date-picker-table {
    background: var(--surface) !important;
  }
  html:not(.light):not(.dark) .v-date-picker-table th {
    color: var(--text2) !important;
  }
  html:not(.light):not(.dark)
    .v-date-picker-table
    .v-btn:not(.v-btn--active):not(.v-btn--disabled) {
    color: var(--text2) !important;
  }
  html:not(.light):not(.dark)
    .v-date-picker-table
    .v-btn:not(.v-btn--active):not(.v-btn--disabled):hover {
    background: var(--sb-subtle) !important;
  }
  html:not(.light):not(.dark) .v-date-picker-table .v-btn.v-btn--active {
    background-color: var(--blue) !important;
    color: #ffffff !important;
  }
  html:not(.light):not(.dark) .v-date-picker-table .v-btn.v-btn--disabled {
    color: var(--text3) !important;
  }
  html:not(.light):not(.dark) .v-date-picker-table__current {
    border-color: var(--blue) !important;
  }

  html:not(.light):not(.dark) .v-date-picker-years {
    background: var(--surface) !important;
  }
  html:not(.light):not(.dark) .v-date-picker-years li {
    color: var(--text2) !important;
  }
  html:not(.light):not(.dark) .v-date-picker-years li.active {
    color: var(--blue) !important;
  }

  html:not(.light):not(.dark) .v-picker__actions {
    background: var(--surface) !important;
    border-top: 1px solid var(--border);
  }
  html:not(.light):not(.dark) .v-picker__actions .v-btn {
    color: var(--blue) !important;
  }
  html:not(.light):not(.dark) .v-picker__actions .error--text {
    color: var(--sb-error) !important;
  }

  html:not(.light):not(.dark) .v-menu__content .v-picker {
    border: 1px solid var(--border) !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4) !important;
  }
}
</style>
